// Admin PKI API layer — typed wrappers over the restful-api admin proxy onto the
// device-CA appliance (restful /v3/admin/pki/*, device-ca/specs/00 §18).
//
// ⚠️ **READ-ONLY.** 憑證簽發已於 2026-08-02 退場(restful-api#140,業主裁決)。
// `POST /v3/admin/pki/devices/{did}/cert` 已移除,打過去是 404。
//
// 依據是 device-ca 2026-06-27 的 **DID 方案變更**:DID 改由 device-ca 自己產生
// (atomic counter + FPE),唯一建立路徑是批量 —— **「對一個外部指定的 DID 當場簽一張」
// 在新模型下沒有對應的原語**。現行入口＝device-ca 的 `devices:batch`(批量首簽)與
// `reissue`(重簽既有 DID),與 ADR-019 的封閉簽發機同向。
//
// SECURITY: the proxy holds the device-CA api key server-side; no key material
// ever reaches the browser (the proxy also refuses to forward it — restful
// `keyMaterialField`).
//
// ⚠️ **時間格式**:本檔的 `not_before`/`not_after` 來自 device-ca,是 **RFC3339 字串**
// (`2026-07-01T00:00:00Z`),**不是**本專案慣用的微秒 epoch。不要拿 `formatTimestampUs`
// 去格式化它 —— 那會回 '—',而且是靜默的。
//
// "Not configured" surfaces as ApiError: 404 (proxy routes off — device-CA not
// wired in this env) or 503 (device-CA unreachable / server key rejected). The
// page renders an honest NotConfigured state for both.

import { apiFetch } from '../../lib/api';

/**
 * 單台憑證 metadata。**這是 device-ca `GET /v1/devices/{did}` 的形狀**,不是憑證 PEM
 * 的解析結果 —— 沒有 `cert_pem`,也沒有 CN/演算法。
 *
 * ⚠️ 本頁此前把查詢結果 cast 成簽發回應的型別(`{did, cert_pem, cert:{cn,…}}`),
 * 兩者的欄位**完全不重疊**。之所以一直沒人發現:restful 側原本打錯上游端點,這條
 * 查詢自 2026-06-27 起一律 503,**渲染路徑從來沒被走到過**(restful-api#140)。
 */
export interface CertRecord {
  device_id: string;
  cert_id: string;
  serial: string;
  fingerprint: string;
  status: string;
  /** RFC3339,非微秒 epoch。 */
  not_before: string;
  /** RFC3339,非微秒 epoch。 */
  not_after: string;
  ca_version: string;
}

/** GET /v3/admin/pki/devices/{did}/cert → cert metadata (no key material). */
export async function lookupCert(did: string): Promise<CertRecord> {
  const res = await apiFetch<CertRecord>(`/v3/admin/pki/devices/${encodeURIComponent(did)}/cert`);
  return res.data;
}

export interface PkiDevice {
  device_id: string;
  status?: string;
  cn?: string;
  serial?: string;
  not_after?: number;
}

/** GET /v3/admin/pki/devices?status=&cursor= → device-ca metadata list (wrapped). */
export async function listDevices(
  status?: string,
  cursor?: string,
): Promise<{ devices: PkiDevice[]; nextCursor: string | null }> {
  const res = await apiFetch<{ devices?: PkiDevice[]; next_cursor?: string | null }>(
    '/v3/admin/pki/devices',
    { query: { status: status ?? '', cursor: cursor ?? '' } },
  );
  const d = res.data ?? {};
  const cur = d.next_cursor;
  return { devices: Array.isArray(d.devices) ? d.devices : [], nextCursor: cur && cur.length > 0 ? cur : null };
}

/** GET /v3/admin/pki/ca-chain → public CA chain PEM (issuing ++ root). */
export async function getCAChain(): Promise<string> {
  const res = await apiFetch<{ ca_chain_pem?: string }>('/v3/admin/pki/ca-chain');
  return res.data?.ca_chain_pem ?? '';
}
